import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { FileText, AlertCircle, Loader2 } from 'lucide-react';
import { mediaUrl } from '../../lib/api';
import { useE2E } from '../../context/E2EContext';
import { useAuth } from '../../context/AuthContext';
import AudioBubble from './AudioBubble';

/**
 * Renders image / video / audio / file attachments.
 * Handles E2E decryption transparently — when the message carries a `crypto`
 * payload, the encrypted blob is unwrapped client-side before display.
 *
 * If the parent supplies `msg._localBlobUrl` (sender just uploaded), the blob
 * is displayed immediately and no re-download happens.
 * If the parent supplies `msg._pending` / `msg._uploadProgress`, an overlay
 * is drawn on top showing the current upload percentage.
 */
export default function MediaBubble({ msg, isOut, onImageClick }) {
  const e2e = useE2E();
  const { user } = useAuth();
  const [blobUrl, setBlobUrl] = useState(msg._localBlobUrl || null);
  const [failed, setFailed] = useState(false);
  const ownedBlobRef = useRef(null); // blob URLs *we* created (need revoking)

  useEffect(() => {
    // Local blob (sender's own upload): show instantly, don't re-download.
    if (msg._localBlobUrl) {
      setBlobUrl(msg._localBlobUrl);
      setFailed(false);
      return;
    }
    if (!msg.file_url) return;
    let cancel = false;
    setFailed(false);
    (async () => {
      try {
        const { data, headers } = await axios.get(mediaUrl(msg.file_url), { responseType: 'blob' });
        if (cancel) return;
        let finalBlob = data;
        if (msg.encrypted && msg.crypto?.file_key_wrapped && e2e?.ready) {
          // The peer is always the other side of the conversation, never ourselves.
          const peerId = msg.sender_id === user?.id ? msg.recipient_id : msg.sender_id;
          const mime = msg.file_type || (msg.type === 'image' ? 'image/jpeg' : msg.type === 'video' ? 'video/mp4' : 'application/octet-stream');
          finalBlob = await e2e.decryptFileFromPeer(peerId, data, msg.crypto, mime);
        } else if (!data.type) {
          const ct = (headers && (headers['content-type'] || headers['Content-Type'])) || 'application/octet-stream';
          finalBlob = new Blob([data], { type: ct });
        }
        if (cancel) return;
        const url = URL.createObjectURL(finalBlob);
        ownedBlobRef.current = url;
        setBlobUrl(url);
      } catch (e) {
        if (!cancel) setFailed(true);
      }
    })();
    return () => {
      cancel = true;
      if (ownedBlobRef.current) {
        URL.revokeObjectURL(ownedBlobRef.current);
        ownedBlobRef.current = null;
      }
    };
  }, [msg.file_url, msg.id, e2e?.ready, msg._localBlobUrl]);

  const pending = !!msg._pending;
  const progress = typeof msg._uploadProgress === 'number' ? msg._uploadProgress : 0;
  const uploadFailed = !!msg._failed;

  const ProgressOverlay = () => (
    <div className="absolute inset-0 bg-black/55 rounded-lg flex flex-col items-center justify-center gap-2 pointer-events-none" data-testid={`msg-upload-progress-${msg.id}`}>
      {uploadFailed ? (
        <>
          <AlertCircle className="w-6 h-6 text-red-400" />
          <div className="text-white text-[11px] font-medium">Invio fallito</div>
        </>
      ) : (
        <>
          <Loader2 className="w-5 h-5 text-white animate-spin" />
          <div className="text-white text-xs font-semibold tabular-nums">{progress}%</div>
          <div className="w-24 h-1 bg-white/20 rounded-full overflow-hidden">
            <div className="h-full bg-[var(--gold)] transition-all duration-150" style={{ width: `${progress}%` }} />
          </div>
        </>
      )}
    </div>
  );

  const FailedOverlay = () => (
    <div className="absolute inset-0 bg-black/70 rounded-lg flex flex-col items-center justify-center gap-1" data-testid={`msg-media-failed-${msg.id}`}>
      <AlertCircle className="w-5 h-5 text-red-400" />
      <div className="text-white text-[11px]">Anteprima non disponibile</div>
    </div>
  );

  if (msg.type === 'image') {
    return (
      <div className="relative inline-block">
        {blobUrl ? (
          <button
            type="button"
            onClick={(e) => { if (pending) return; e.stopPropagation(); onImageClick(blobUrl, msg.file_name || ''); }}
            className={`block ${pending ? 'cursor-wait' : 'cursor-zoom-in'}`}
            data-testid="msg-image-btn"
          >
            <img src={blobUrl} alt={msg.file_name} className="rounded-lg max-w-[240px] max-h-[280px] object-cover" data-testid="msg-image" />
          </button>
        ) : (
          <div className="w-40 h-40 bg-black/25 rounded-lg animate-pulse" />
        )}
        {(pending || uploadFailed) && <ProgressOverlay />}
        {!pending && !uploadFailed && failed && <FailedOverlay />}
      </div>
    );
  }
  if (msg.type === 'video') {
    return (
      <div className="relative inline-block">
        {blobUrl ? (
          <video controls src={blobUrl} className="rounded-lg max-w-[280px]" data-testid="msg-video" />
        ) : (
          <div className="w-64 h-40 bg-black/25 rounded-lg animate-pulse" />
        )}
        {(pending || uploadFailed) && <ProgressOverlay />}
        {!pending && !uploadFailed && failed && <FailedOverlay />}
      </div>
    );
  }
  if (msg.type === 'audio') {
    return <AudioBubble msg={msg} isOut={isOut} />;
  }
  return (
    <a
      href={blobUrl || '#'}
      download={msg.file_name}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-3 min-w-[200px] relative"
      data-testid="msg-file"
    >
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isOut ? 'bg-black/20' : 'bg-[var(--gold-soft)]'}`}>
        <FileText className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate">{msg.file_name || 'File'}</div>
        <div className="text-[10px] opacity-60">
          {pending ? (uploadFailed ? 'Invio fallito' : `Invio in corso… ${progress}%`) : (failed ? 'Non disponibile' : 'Tocca per scaricare')}
        </div>
      </div>
    </a>
  );
}
